import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Public signer page. Reached via the tokenised URL in the signing email:
//   /app/#/sign?e=<envelope_id>&t=<token>
// No auth required — the token IS the credential. Renders the envelope,
// lists the fields assigned to this signer, captures a typed signature +
// (optional) 6-digit OTP, then flips the envelope to signed/completed via
// the existing public API under /server/api/sign/...
//
// We deliberately skip visual PDF placement for v1 — the cert-of-completion
// the server generates on envelope.complete captures everything the Taxing
// Master / ECTA requires. Visual drag-drop placement is a later iteration.

type EnvelopeInfo = {
  envelope: {
    id: number | string;
    title: string;
    message: string | null;
    source_document_id: number;
    expires_at: string | null;
  };
  signer: {
    id: number | string;
    name: string;
    email: string;
    require_otp: boolean;
    status: string;
  };
  fields: Array<{
    ROWID: number | string;
    page: number;
    field_type: 'signature' | 'initial' | 'date' | 'text' | 'checkbox';
    required: boolean | string;
    value: string | null;
  }>;
};

export default function SignerPage() {
  const [params] = useSearchParams();
  const envelopeId = params.get('e') || '';
  const token      = params.get('t') || '';

  const [info, setInfo] = useState<EnvelopeInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!envelopeId || !token) {
      setLoadError('Missing envelope id or token. Open the full link from your signing email.');
      return;
    }
    (async () => {
      try {
        const r = await fetch(`/server/api/sign/${envelopeId}?t=${encodeURIComponent(token)}`);
        if (!r.ok) {
          const body = await r.json().catch(() => ({ error: { message: r.statusText } }));
          throw new Error(body?.error?.message || `Server returned ${r.status}`);
        }
        const data = (await r.json()) as EnvelopeInfo;
        setInfo(data);
        // Default typed-signature fields to the signer's full name so one
        // click can sign — signer can still edit before submitting.
        const defaults: Record<string, string> = {};
        for (const f of data.fields) {
          if (f.value) defaults[String(f.ROWID)] = f.value;
          else if (f.field_type === 'signature' || f.field_type === 'initial') defaults[String(f.ROWID)] = data.signer.name;
          else if (f.field_type === 'date') defaults[String(f.ROWID)] = new Date().toISOString().slice(0, 10);
        }
        setValues(defaults);
      } catch (e) {
        setLoadError((e as Error).message);
      }
    })();
  }, [envelopeId, token]);

  const sendOtp = async () => {
    setOtpError(null);
    try {
      const r = await fetch(`/server/api/sign/${envelopeId}/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token }),
      });
      if (!r.ok) throw new Error(`Could not send OTP (${r.status})`);
      setOtpSent(true);
    } catch (e) {
      setOtpError((e as Error).message);
    }
  };

  const submit = async () => {
    if (!info) return;
    setSubmitting(true); setSubmitError(null);
    try {
      // Validate required fields.
      for (const f of info.fields) {
        const req = f.required === true || f.required === 'true';
        if (!req) continue;
        const v = values[String(f.ROWID)] || '';
        if (f.field_type === 'checkbox') {
          if (v !== 'true') throw new Error(`Please tick the required box before signing.`);
        } else if (!v.trim()) {
          throw new Error(`Please complete all required fields.`);
        }
      }
      if (info.signer.require_otp && !otp.trim()) {
        throw new Error('Enter the 6-digit code we emailed you.');
      }

      // 1) Persist field values.
      const payload: Record<string, unknown> = {
        t: token,
        values: Object.entries(values).map(([field_id, value]) => ({ field_id, value })),
      };
      if (info.signer.require_otp) payload.otp = otp.trim();

      const r1 = await fetch(`/server/api/sign/${envelopeId}/fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r1.ok) {
        const body = await r1.json().catch(() => ({ error: { message: r1.statusText } }));
        throw new Error(body?.error?.message || `Field submit failed (${r1.status})`);
      }

      // 2) Mark the signer as signed.
      // The "typed signature" we capture is the signature field value — use
      // it as the audit-trail typed_text regardless of how many fields there
      // were. Picks the signature field first, else the first field.
      const sigField = info.fields.find((f) => f.field_type === 'signature');
      const typed = sigField ? values[String(sigField.ROWID)] : info.signer.name;
      const r2 = await fetch(`/server/api/sign/${envelopeId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token, signature: { typed_text: typed } }),
      });
      if (!r2.ok) {
        const body = await r2.json().catch(() => ({ error: { message: r2.statusText } }));
        throw new Error(body?.error?.message || `Sign failed (${r2.status})`);
      }
      setDone(true);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally { setSubmitting(false); }
  };

  if (loadError) {
    return <Shell><div style={errorCard}>{loadError}</div></Shell>;
  }
  if (!info) {
    return <Shell><div className="muted">Loading…</div></Shell>;
  }
  if (done) {
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>✓</div>
          <h2 style={{ marginTop: 0 }}>Thank you — you've signed.</h2>
          <p className="muted" style={{ fontSize: 14 }}>
            An audit trail and the certificate of completion will be sent to {info.signer.email} once every signer has completed.
          </p>
          <p className="muted" style={{ fontSize: 12, marginTop: 18 }}>
            You can verify this signature at any time at <code>/verify/{String(info.envelope.id)}</code>.
          </p>
        </div>
      </Shell>
    );
  }

  const alreadySigned = info.signer.status === 'signed';

  return (
    <Shell>
      <div style={{ marginBottom: 18 }}>
        <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Signing request</div>
        <h1 style={{ fontSize: 22, margin: '6px 0' }}>{info.envelope.title}</h1>
        <div className="muted" style={{ fontSize: 13 }}>Signer: {info.signer.name} · {info.signer.email}</div>
        {info.envelope.expires_at && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Expires: {String(info.envelope.expires_at).slice(0, 10)}</div>
        )}
      </div>

      {info.envelope.message && (
        <div className="card" style={{ marginBottom: 18, background: 'var(--color-surface-2)' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Message</div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{info.envelope.message}</div>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <a href={`/server/api/sign/${info.envelope.id}/document?t=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer">
          View the document →
        </a>
        <span className="muted" style={{ fontSize: 12, marginLeft: 12 }}>
          (opens in a new tab; the link above came from your signing email)
        </span>
      </div>

      {alreadySigned ? (
        <div className="card" style={{ background: 'var(--color-accent-soft)', borderColor: 'var(--color-accent)' }}>
          <b>You've already signed this envelope.</b>
          <p className="muted" style={{ fontSize: 13, margin: '6px 0 0' }}>
            No further action is required. You can verify your signature at <code>/verify/{String(info.envelope.id)}</code> at any time.
          </p>
        </div>
      ) : (
        <div className="stack">
          {info.fields.map((f) => {
            const id = String(f.ROWID);
            const req = f.required === true || f.required === 'true';
            const labelByType: Record<string, string> = {
              signature: 'Type your full name to sign',
              initial:   'Type your initials',
              date:      'Date',
              text:      'Text',
              checkbox:  'Confirm',
            };
            if (f.field_type === 'checkbox') {
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={values[id] === 'true'}
                    onChange={(e) => setValues({ ...values, [id]: e.target.checked ? 'true' : '' })}
                    style={{ width: 'auto' }}
                  />
                  I agree{req && <span style={{ color: 'var(--color-danger)' }}> *</span>}
                </label>
              );
            }
            return (
              <div key={id}>
                <label>
                  {labelByType[f.field_type] || f.field_type}
                  {req && <span style={{ color: 'var(--color-danger)' }}> *</span>}
                </label>
                <input
                  type={f.field_type === 'date' ? 'date' : 'text'}
                  value={values[id] || ''}
                  onChange={(e) => setValues({ ...values, [id]: e.target.value })}
                  placeholder={info.signer.name}
                  style={f.field_type === 'signature' || f.field_type === 'initial'
                    ? { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20, fontStyle: 'italic' }
                    : undefined}
                />
              </div>
            );
          })}

          {info.signer.require_otp && (
            <div className="card" style={{ background: 'var(--color-surface-2)' }}>
              <div className="row">
                <div className="grow">
                  <label>6-digit code (sent to {info.signer.email})</label>
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={6}
                  />
                </div>
                <button onClick={sendOtp} disabled={otpSent} style={{ alignSelf: 'flex-end' }}>
                  {otpSent ? 'Code sent' : 'Send code'}
                </button>
              </div>
              {otpError && <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 6 }}>{otpError}</div>}
              {otpSent && !otpError && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Didn't arrive? Check spam, then click <button onClick={() => { setOtpSent(false); sendOtp(); }} style={{ padding: '2px 6px', fontSize: 12 }}>resend</button>.
                </div>
              )}
            </div>
          )}

          {submitError && <div style={errorCard}>{submitError}</div>}

          <button className="primary" onClick={submit} disabled={submitting} style={{ fontSize: 15, padding: '12px 28px', alignSelf: 'flex-end' }}>
            {submitting ? 'Signing…' : 'Sign now'}
          </button>

          <p className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 12 }}>
            By clicking "Sign now" you confirm that your typed signature has the same legal effect as a handwritten one.
            Your IP address and timestamp are captured as part of the audit trail.
          </p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ maxWidth: 640, margin: '60px auto', padding: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/app/litigate-logo.png" alt="Litigate" style={{ height: 36 }} />
      </div>
      <div className="card">{children}</div>
    </div>
  );
}

const errorCard: React.CSSProperties = {
  padding: 12,
  borderRadius: 8,
  background: 'var(--color-danger-soft)',
  color: 'var(--color-danger)',
  fontSize: 13,
};
